/**
 * QuizStage - In-page quiz renderer for the new learning page
 *
 * Displays quizzes inline (not as a full-screen overlay).
 * Uses learningSessionApi to submit responses and complete sessions.
 * Shows immediate feedback after each question.
 */

import { useState, useCallback, useMemo } from 'react'
import {
    ChevronLeft,
    ChevronRight,
    Loader2,
    ArrowLeft,
    CheckCircle,
    XCircle,
    Trophy,
    RefreshCw,
} from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { cn } from '@/lib/utils'
import { quizScriptApi } from '@/api/quizScript'
import { type QuizBehaviorOptions, LESSON_QUIZ_OPTIONS } from '@/types/quiz'
import {
    learningSessionApi,
    type QuizDataResponse,
    type QuizResponseResponse,
    type CompleteSessionResponse,
} from '@/api/learningSession'
import type {
    QuizScript,
    QuizSolveResult,
    MultiChoiceQuizData,
    QuizData,
} from '@quebon/shared-learning'
import {
    QuizRenderer,
} from '@quebon/shared-learning'

// ============================================================================
// Types
// ============================================================================

export interface QuizStageProps {
    lessonId: number
    lessonTitle: string
    componentProgressId: number
    sessionId: number
    quizData: QuizDataResponse
    quizLocation: 'pre' | 'post' | 'wrapup' | 'checkup'
    classProductId?: number        // B2B: Configuration 조회용 (레슨 완료 판단에 필요)
    onComplete: (response: CompleteSessionResponse) => void
    onBack: () => void
    /** 퀴즈 동작 옵션 (기본값: LESSON_QUIZ_OPTIONS) */
    behaviorOptions?: QuizBehaviorOptions
    /** 시도 횟수 (1=첫 시도, 2+=재시도) - 1회차에서는 완료 화면 건너뜀 */
    attemptNumber?: number
    /** 완료 화면 건너뛰기 (모듈 마무리 퀴즈 등 상위에서 완료 화면을 처리하는 경우) */
    skipCompletionScreen?: boolean
}

type QuizStatus = 'unsolved' | 'current' | 'correct' | 'incorrect' | 'submitted'

interface QuizResultInfo {
    isCorrect: boolean
    solveResult: QuizSolveResult
    responseData: QuizResponseResponse
}

// ============================================================================
// Helper Functions
// ============================================================================

function getQuizTitle(location: 'pre' | 'post' | 'wrapup' | 'checkup', t: (key: string) => string): string {
    switch (location) {
        case 'pre':
            return t('learning.quiz.titlePre')
        case 'post':
            return t('learning.quiz.titlePost')
        case 'wrapup':
            return t('learning.quiz.titleWrapup')
        case 'checkup':
            return t('learning.quiz.titleCheckup')
        default:
            return t('learning.quiz.titleDefault')
    }
}

function getQuizDescription(location: 'pre' | 'post' | 'wrapup' | 'checkup', t: (key: string) => string): string {
    switch (location) {
        case 'pre':
            return t('learning.quiz.descPre')
        case 'post':
            return t('learning.quiz.descPost')
        case 'wrapup':
            return t('learning.quiz.descWrapup')
        case 'checkup':
            return t('learning.quiz.descCheckup')
        default:
            return t('learning.quiz.descDefault')
    }
}

// ============================================================================
// Component
// ============================================================================

export function QuizStage({
    lessonId,
    lessonTitle,
    componentProgressId,
    sessionId,
    quizData,
    quizLocation,
    classProductId,
    onComplete,
    onBack,
    behaviorOptions = LESSON_QUIZ_OPTIONS,
    attemptNumber = 1,
    skipCompletionScreen = false,
}: QuizStageProps) {
    const { t } = useTranslation('learner')

    // Destructure behavior options
    const {
        sequentialNavigation,
        showImmediateFeedback,
        allowRetry,
        passScore,
    } = behaviorOptions

    // State - Initialize currentIndex from resumeFromIndex if available (session resume support)
    const [quizScripts, setQuizScripts] = useState<Map<string, QuizScript>>(new Map())
    const [currentIndex, setCurrentIndex] = useState(() => {
        // Resume from previous session if resumeFromIndex is provided
        if (quizData.resumeFromIndex != null && quizData.resumeFromIndex > 0) {
            return quizData.resumeFromIndex
        }
        return 0
    })
    const [resultsByQuizId, setResultsByQuizId] = useState<Map<string, QuizResultInfo>>(() => {
        // Restore previous responses from session resume (Issue #4.3)
        // 즉시 정오답 노출 설정 시, 이전에 푼 문제의 해설도 함께 복원
        const initialMap = new Map<string, QuizResultInfo>()
        if (quizData.previousResponses && quizData.previousResponses.length > 0) {
            quizData.previousResponses.forEach(prev => {
                // 해설 정보 변환 (backend QuizDescriptionResponse → QuizSolveResult description)
                const descriptionForSolveResult = prev.description ? {
                    text: prev.description.text ?? undefined,
                    image: prev.description.image ? {
                        signedUrl: (prev.description.image.signedUrl ?? prev.description.image.url ?? prev.description.image.cloudfrontUrl) || undefined,
                        scaleX: prev.description.image.scaleX ?? undefined,
                        scaleY: prev.description.image.scaleY ?? undefined,
                    } : undefined,
                    imageMobile: prev.description.imageMobile ? {
                        signedUrl: (prev.description.imageMobile.signedUrl ?? prev.description.imageMobile.url ?? prev.description.imageMobile.cloudfrontUrl) || undefined,
                        scaleX: prev.description.imageMobile.scaleX ?? undefined,
                        scaleY: prev.description.imageMobile.scaleY ?? undefined,
                    } : undefined,
                    desmos: prev.description.desmos ?? undefined,
                } : undefined

                const solveResult: QuizSolveResult = {
                    correct: prev.isCorrect,
                    corrects: (prev.corrects ?? []).map(c => Boolean(c)),
                    correctAnswers: (prev.correctAnswers ?? []).map(a => String(a)),
                    elapsedTime: 0,
                    description: descriptionForSolveResult,
                }
                initialMap.set(prev.quizId, {
                    isCorrect: prev.isCorrect,
                    solveResult,
                    responseData: {
                        responseId: '',  // Not needed for display
                        quizId: prev.quizId,  // Keep as string
                        isCorrect: prev.isCorrect,
                        corrects: prev.corrects ?? undefined,
                        correctAnswers: prev.correctAnswers ?? undefined,
                        description: prev.description ?? undefined,
                    },
                })
            })
        }
        return initialMap
    })
    const [submittedAnswersByQuizId, setSubmittedAnswersByQuizId] = useState<Map<string, string[]>>(() => {
        // Restore previous submitted answers from session resume
        const initialMap = new Map<string, string[]>()
        if (quizData.previousResponses && quizData.previousResponses.length > 0) {
            quizData.previousResponses.forEach(prev => {
                if (prev.submittedResponse && Array.isArray(prev.submittedResponse)) {
                    initialMap.set(prev.quizId, prev.submittedResponse.map(a => String(a)))
                }
            })
        }
        return initialMap
    })
    // 이전 응답의 varInstantiation 저장 (재진입 시 동일한 변수 값으로 문제 표시)
    const [varsByQuizId] = useState<Map<string, Record<string, unknown>>>(() => {
        const initialMap = new Map<string, Record<string, unknown>>()
        if (quizData.previousResponses && quizData.previousResponses.length > 0) {
            quizData.previousResponses.forEach(prev => {
                if (prev.varInstantiation) {
                    initialMap.set(prev.quizId, prev.varInstantiation)
                }
            })
        }
        return initialMap
    })
    const [isSolving, setIsSolving] = useState(false)
    const [isLoadingQuiz, setIsLoadingQuiz] = useState(false)
    const [isCompleting, setIsCompleting] = useState(false)
    const [isCompleted, setIsCompleted] = useState(false)
    const [completionResponse, setCompletionResponse] = useState<CompleteSessionResponse | null>(null)
    // 채점결과 확인 모드: 완료 후 문제 리뷰 시 true
    const [isReviewMode, setIsReviewMode] = useState(false)

    // 방금 푼 문제 ID (현재 세션에서 소리가 재생된 문제 추적)
    const [soundPlayedQuizIds, setSoundPlayedQuizIds] = useState<Set<string>>(new Set())

    // 현재 입력 중인 답변 (아직 제출 안됨) - onAnswerChange 콜백으로 업데이트
    const [pendingAnswersByQuizId, setPendingAnswersByQuizId] = useState<Map<string, string[]>>(new Map())

    // Track already answered quiz IDs from session
    const answeredQuizIdsSet = useMemo(() => {
        return new Set(quizData.answeredQuizIds || [])
    }, [quizData.answeredQuizIds])

    // Computed values
    const quizItems = quizData.quizzes
    const currentQuizItem = quizItems[currentIndex]
    const currentQuizId = currentQuizItem?.quizId ?? ''
    const currentResult = resultsByQuizId.get(currentQuizId)
    const currentSubmittedAnswers = submittedAnswersByQuizId.get(currentQuizId)
    const currentQuizScript = quizScripts.get(currentQuizId)
    const currentPendingAnswers = pendingAnswersByQuizId.get(currentQuizId)

    // 배열 비교 헬퍼 함수
    const arraysEqual = useCallback((a?: string[], b?: string[]): boolean => {
        if (!a && !b) return true
        if (!a || !b) return false
        if (a.length !== b.length) return false
        return a.every((val, idx) => val === b[idx])
    }, [])

    // 퀴즈 컴포넌트에서 답변 변경 시 호출되는 콜백
    const handleAnswerChange = useCallback((answers: string[]) => {
        setPendingAnswersByQuizId(prev => new Map(prev).set(currentQuizId, answers))
    }, [currentQuizId])

    // Load quiz script for current quiz if not already loaded
    const loadQuizScript = useCallback(async (quizId: string) => {
        if (quizScripts.has(quizId)) return quizScripts.get(quizId)!

        setIsLoadingQuiz(true)
        try {
            // 재진입 시 이전에 제출할 때 사용된 vars로 문제 로드 (일관성 유지)
            const storedVars = varsByQuizId.get(quizId)
            const script = await quizScriptApi.getQuiz(quizId, false, storedVars)
            setQuizScripts(prev => new Map(prev).set(quizId, script))
            return script
        } catch (error) {
            console.error('Failed to load quiz script:', error)
            return null
        } finally {
            setIsLoadingQuiz(false)
        }
    }, [quizScripts, varsByQuizId])

    // Load current quiz on mount and when index changes
    useState(() => {
        if (currentQuizId) {
            loadQuizScript(currentQuizId)
        }
    })

    // Get quiz status for navigation
    const getQuizStatus = useCallback((index: number): QuizStatus => {
        if (index === currentIndex) return 'current'

        const quiz = quizItems[index]
        const quizId = quiz?.quizId ?? ''
        const result = resultsByQuizId.get(quizId)

        // Result is now properly populated from previousResponses on session resume
        if (!result) return 'unsolved'

        // 즉시 정오답 표시 안함: 제출 완료 상태로 표시
        if (!showImmediateFeedback) return 'submitted'

        return result.isCorrect ? 'correct' : 'incorrect'
    }, [currentIndex, quizItems, resultsByQuizId, showImmediateFeedback])

    // Convert QuizScript to legacy QuizData format
    const getLegacyQuizData = useCallback((quizScript: QuizScript): QuizData | null => {
        if (!quizScript) return null

        const rawData = quizScript as unknown as Record<string, unknown>
        const qd = (rawData.quizData as QuizData) || quizScript

        return {
            ...qd,
            type: quizScript.type || qd.type,
            title: quizScript.title || qd.title,
            problem: quizScript.problem || qd.problem,
            vars: quizScript.vars || qd.vars,
            image: quizScript.image || qd.image,
            imageMobile: quizScript.imageMobile || qd.imageMobile,
        } as QuizData
    }, [])

    // 역변환 함수: 백엔드에서 저장된 변환된 답변을 원본 형식으로 복원
    const reverseTransformAnswers = useCallback((
        quizScript: QuizScript,
        submittedResponse: unknown[]
    ): string[] => {
        const legacyQuiz = getLegacyQuizData(quizScript)
        if (!legacyQuiz) return submittedResponse.map(a => String(a))

        switch (legacyQuiz.type) {
            case 'multi_choice': {
                // 인덱스(1-based) → choice key 역변환
                const quiz = legacyQuiz as MultiChoiceQuizData
                const sortedChoices =
                    quiz.random && quiz.random.length > 0
                        ? quiz.random.map((idx) => quiz.choices[idx]).filter(Boolean)
                        : quiz.choices
                return submittedResponse.map(idx => {
                    const index = Number(idx) - 1
                    return sortedChoices[index]?.key ?? String(idx)
                })
            }
            case 'boolean':
            case 'select_text':
            case 'fill_blank':
            case 'drag_text':
            default:
                return submittedResponse.map(a => String(a))
        }
    }, [getLegacyQuizData])

    // 이전 답변 역변환 (세션 재진입 시)
    useState(() => {
        if (quizData.previousResponses && quizData.previousResponses.length > 0) {
            quizData.previousResponses.forEach(async (prev) => {
                if (prev.submittedResponse && Array.isArray(prev.submittedResponse)) {
                    try {
                        // varInstantiation으로 퀴즈 로드 (이전과 동일한 변수 값 사용)
                        const storedVars = prev.varInstantiation ?? undefined
                        const script = await quizScriptApi.getQuiz(prev.quizId, false, storedVars)
                        setQuizScripts(prevScripts => new Map(prevScripts).set(prev.quizId, script))
                        const reversedAnswers = reverseTransformAnswers(script, prev.submittedResponse)
                        setSubmittedAnswersByQuizId(prevMap => new Map(prevMap).set(prev.quizId, reversedAnswers))
                    } catch (error) {
                        console.error('Failed to load quiz script for reverse transform:', error)
                    }
                }
            })
        }
    })

    // Handle quiz solve
    const handleSolve = useCallback(
        async (
            _quizId: string | number,
            answers: string[],
            quizVars: Record<string, unknown>,
            elapsedTime: number
        ) => {
            if (!currentQuizScript || isSolving) return

            const quizId = currentQuizId
            // NOTE: Do NOT use parseInt - quizId is a Snowflake ID that exceeds JS Number.MAX_SAFE_INTEGER
            // Keep as string to preserve precision

            setSubmittedAnswersByQuizId(prev => new Map(prev).set(quizId, answers))
            setIsSolving(true)

            const legacyQuiz = getLegacyQuizData(currentQuizScript)
            if (!legacyQuiz) {
                setIsSolving(false)
                return
            }

            try {
                // Transform answers based on quiz type for the solve API
                let transformedAnswers: (string | number | boolean)[] = []

                switch (legacyQuiz.type) {
                    case 'fill_blank':
                        transformedAnswers = answers
                        break

                    case 'multi_choice': {
                        const quiz = legacyQuiz as MultiChoiceQuizData
                        transformedAnswers = answers
                            .map((key) => {
                                const index = quiz.choices.findIndex((choice) => choice.key === key)
                                return index >= 0 ? index + 1 : 0
                            })
                            .filter((idx) => idx > 0)
                        break
                    }

                    case 'select_text': {
                        transformedAnswers = answers.map((a) => {
                            const num = parseInt(a, 10)
                            return isNaN(num) ? 0 : num
                        })
                        break
                    }

                    case 'boolean': {
                        transformedAnswers = answers.map((a) => a === 'true')
                        break
                    }

                    case 'drag_text':
                        transformedAnswers = answers
                        break

                    default:
                        transformedAnswers = []
                }

                // Submit to unified learning session API (includes solve + progress tracking)
                const quizResponseRequest = {
                    quizId: quizId,  // Keep as string to preserve Snowflake ID precision
                    scriptId: currentQuizScript.scriptId || null,
                    answers: transformedAnswers,
                    vars: quizVars ?? {},  // Always send object, never null
                    timeSpentSec: Math.floor(elapsedTime / 1000),
                }

                const responseData = await learningSessionApi.submitQuizResponse(
                    sessionId,
                    quizResponseRequest
                )

                // Convert unified API response to QuizSolveResult
                const solveResult: QuizSolveResult = {
                    correct: responseData.isCorrect,
                    corrects: (responseData.corrects ?? []).map((c) => {
                        if (typeof c === 'boolean') return c
                        if (c === 'true' || c === true) return true
                        if (c === 'false' || c === false) return false
                        return Boolean(c)
                    }),
                    correctAnswers: (responseData.correctAnswers ?? []).map((a) => {
                        if (typeof a === 'string') return a
                        if (typeof a === 'number') return String(a)
                        if (typeof a === 'boolean') return String(a)
                        return String(a)
                    }),
                    elapsedTime,
                    description: responseData.description
                        ? {
                            text: responseData.description.text ?? undefined,
                            image: responseData.description.image
                                ? {
                                    signedUrl: (responseData.description.image.signedUrl ?? responseData.description.image.url ?? responseData.description.image.cloudfrontUrl) || undefined,
                                    scaleX: responseData.description.image.scaleX ?? undefined,
                                    scaleY: responseData.description.image.scaleY ?? undefined,
                                }
                                : undefined,
                            imageMobile: responseData.description.imageMobile
                                ? {
                                    signedUrl: (responseData.description.imageMobile.signedUrl ?? responseData.description.imageMobile.url ?? responseData.description.imageMobile.cloudfrontUrl) || undefined,
                                    scaleX: responseData.description.imageMobile.scaleX ?? undefined,
                                    scaleY: responseData.description.imageMobile.scaleY ?? undefined,
                                }
                                : undefined,
                            desmos: responseData.description.desmos ?? undefined,
                        }
                        : undefined,
                }

                setResultsByQuizId(prev => new Map(prev).set(quizId, {
                    isCorrect: responseData.isCorrect,
                    solveResult,
                    responseData,
                }))

                // allowRetry: 제출 후 처리
                if (allowRetry) {
                    const isLastQuestion = currentIndex >= quizItems.length - 1
                    if (isLastQuestion) {
                        // 마지막 문제: 완료 처리
                        try {
                            const response = await learningSessionApi.completeSession(componentProgressId, {
                                componentType: 'quiz',
                                forceComplete: false,
                                classProductId,
                                passScore,
                            })
                            // skipCompletionScreen이 true면 바로 onComplete 호출 (모듈 마무리 퀴즈 등)
                            if (skipCompletionScreen) {
                                onComplete(response)
                            } else {
                                setCompletionResponse(response)
                                setIsCompleted(true)
                            }
                        } catch (completeErr) {
                            console.error('Failed to complete session:', completeErr)
                        }
                    } else {
                        // 다음 문제로 이동
                        const nextIndex = currentIndex + 1
                        setCurrentIndex(nextIndex)
                        const nextQuizId = quizItems[nextIndex]?.quizId
                        if (nextQuizId) {
                            loadQuizScript(nextQuizId)
                        }
                    }
                }
            } catch (err) {
                console.error('Failed to solve quiz:', err)

                // Create error result
                const errorResult: QuizSolveResult = {
                    correct: false,
                    corrects: [],
                    correctAnswers: [],
                    elapsedTime,
                }
                setResultsByQuizId(prev => new Map(prev).set(quizId, {
                    isCorrect: false,
                    solveResult: errorResult,
                    responseData: {
                        responseId: '',
                        quizId: quizId,  // Keep as string
                        isCorrect: false,
                    },
                }))
            } finally {
                setIsSolving(false)
            }
        },
        [currentQuizScript, currentQuizId, isSolving, getLegacyQuizData, sessionId, allowRetry, currentIndex, quizItems, loadQuizScript, componentProgressId, classProductId, passScore, onComplete, attemptNumber, skipCompletionScreen]
    )

    // Navigation handlers
    // 현재 문제의 pending 답변을 제출하고 결과 반환
    const submitCurrentPendingAnswer = useCallback(async (): Promise<boolean> => {
        const quizId = currentQuizId
        const pending = pendingAnswersByQuizId.get(quizId)
        const submitted = submittedAnswersByQuizId.get(quizId)

        // 답변이 없거나, 모두 빈값이거나, 이미 제출된 답변과 동일하면 스킵
        if (!pending || pending.length === 0 || pending.every(a => a === '') || arraysEqual(pending, submitted)) {
            return true
        }

        // 퀴즈 스크립트가 없으면 스킵
        if (!currentQuizScript) {
            return true
        }

        // handleSolve와 동일한 로직으로 답변 제출 (startTime은 0으로 설정)
        const elapsedTime = 0
        const legacyQuiz = getLegacyQuizData(currentQuizScript)
        if (!legacyQuiz) return true

        try {
            // Transform answers based on quiz type
            let transformedAnswers: (string | number | boolean)[] = []

            switch (legacyQuiz.type) {
                case 'fill_blank':
                    transformedAnswers = pending
                    break
                case 'multi_choice': {
                    const quiz = legacyQuiz as MultiChoiceQuizData
                    transformedAnswers = pending
                        .map((key) => {
                            const index = quiz.choices.findIndex((choice) => choice.key === key)
                            return index >= 0 ? index + 1 : 0
                        })
                        .filter((idx) => idx > 0)
                    break
                }
                case 'select_text':
                    transformedAnswers = pending.map((a) => {
                        const num = parseInt(a, 10)
                        return isNaN(num) ? 0 : num
                    })
                    break
                case 'boolean':
                    transformedAnswers = pending.map((a) => a === 'true')
                    break
                case 'drag_text':
                    transformedAnswers = pending
                    break
                default:
                    transformedAnswers = []
            }

            const quizResponseRequest = {
                quizId: quizId,
                scriptId: currentQuizScript.scriptId || null,
                answers: transformedAnswers,
                vars: currentQuizScript.vars ?? {},
                timeSpentSec: Math.floor(elapsedTime / 1000),
            }

            const responseData = await learningSessionApi.submitQuizResponse(sessionId, quizResponseRequest)

            // Update results
            const solveResult: QuizSolveResult = {
                correct: responseData.isCorrect,
                corrects: (responseData.corrects ?? []).map((c) => Boolean(c)),
                correctAnswers: (responseData.correctAnswers ?? []).map((a) => String(a)),
                elapsedTime,
                description: responseData.description ? {
                    text: responseData.description.text ?? undefined,
                    image: responseData.description.image ? {
                        signedUrl: (responseData.description.image.signedUrl ?? responseData.description.image.url ?? responseData.description.image.cloudfrontUrl) || undefined,
                    } : undefined,
                } : undefined,
            }

            setResultsByQuizId(prev => new Map(prev).set(quizId, {
                isCorrect: responseData.isCorrect,
                solveResult,
                responseData,
            }))
            setSubmittedAnswersByQuizId(prev => new Map(prev).set(quizId, pending))

            return true
        } catch (err) {
            console.error('Failed to auto-save quiz answer:', err)
            return false
        }
    }, [currentQuizId, pendingAnswersByQuizId, submittedAnswersByQuizId, arraysEqual, currentQuizScript, getLegacyQuizData, sessionId])

    const handlePrevious = useCallback(async () => {
        if (currentIndex > 0) {
            // allowRetry인 경우 이동 전 자동 저장
            if (allowRetry) {
                await submitCurrentPendingAnswer()
            }
            // 현재 문제가 풀렸으면 소리 재생 완료로 표시
            if (resultsByQuizId.has(currentQuizId)) {
                setSoundPlayedQuizIds(prev => new Set(prev).add(currentQuizId))
            }
            const newIndex = currentIndex - 1
            setCurrentIndex(newIndex)
            const newQuizId = quizItems[newIndex]?.quizId
            if (newQuizId) {
                loadQuizScript(newQuizId)
            }
        }
    }, [currentIndex, quizItems, loadQuizScript, allowRetry, submitCurrentPendingAnswer, currentQuizId, resultsByQuizId])

    const handleNext = useCallback(async () => {
        if (currentIndex < quizItems.length - 1) {
            // allowRetry인 경우 이동 전 자동 저장
            if (allowRetry) {
                await submitCurrentPendingAnswer()
            }
            // 현재 문제가 풀렸으면 소리 재생 완료로 표시
            if (resultsByQuizId.has(currentQuizId)) {
                setSoundPlayedQuizIds(prev => new Set(prev).add(currentQuizId))
            }
            const newIndex = currentIndex + 1
            setCurrentIndex(newIndex)
            const newQuizId = quizItems[newIndex]?.quizId
            if (newQuizId) {
                loadQuizScript(newQuizId)
            }
        }
    }, [currentIndex, quizItems, loadQuizScript, allowRetry, submitCurrentPendingAnswer, currentQuizId, resultsByQuizId])

    // 퀴즈 컴포넌트에 전달할 solveResult 결정
    const getSolveResultForDisplay = useCallback((quizId: string): QuizSolveResult | undefined => {
        const result = resultsByQuizId.get(quizId)
        if (!result) return undefined  // 아직 안 풀었음

        // 리뷰 모드: 전체 결과 표시 (정답, 해설 포함)
        if (isReviewMode) {
            return result.solveResult
        }

        if (allowRetry) {
            // 다시 풀기 허용: solveResult 전달 안함 → 제출 버튼 활성화
            return undefined
        }

        if (!showImmediateFeedback) {
            // 즉시 정오답 표시 안함: 필터링된 결과 반환
            return {
                ...result.solveResult,
                corrects: [],
                correctAnswers: [],
                description: undefined,
            }
        }

        // 기본: 전체 결과 표시
        return result.solveResult
    }, [resultsByQuizId, allowRetry, showImmediateFeedback, isReviewMode])

    // 특정 인덱스로 이동 가능한지 확인
    const canNavigateTo = useCallback((index: number): boolean => {
        if (!sequentialNavigation) return true
        // 순차 네비게이션: 이미 푼 문제 또는 현재 문제까지만 이동 가능
        if (index <= currentIndex) return true
        // 이전 문제들이 모두 풀렸는지 확인
        for (let i = 0; i < index; i++) {
            const quizId = quizItems[i]?.quizId
            if (quizId && !resultsByQuizId.has(quizId)) return false
        }
        return true
    }, [sequentialNavigation, currentIndex, quizItems, resultsByQuizId])

    const handleNavClick = useCallback(async (index: number) => {
        if (index === currentIndex) return
        // 순차 네비게이션 체크
        if (!canNavigateTo(index)) return
        // allowRetry인 경우 이동 전 자동 저장
        if (allowRetry) {
            await submitCurrentPendingAnswer()
        }
        // 현재 문제가 풀렸으면 소리 재생 완료로 표시
        if (resultsByQuizId.has(currentQuizId)) {
            setSoundPlayedQuizIds(prev => new Set(prev).add(currentQuizId))
        }
        setCurrentIndex(index)
        const newQuizId = quizItems[index]?.quizId
        if (newQuizId) {
            loadQuizScript(newQuizId)
        }
    }, [currentIndex, quizItems, loadQuizScript, canNavigateTo, allowRetry, submitCurrentPendingAnswer, currentQuizId, resultsByQuizId])

    // Check if can proceed to next
    const canGoNext = useMemo(() => {
        if (currentIndex >= quizItems.length - 1) return false

        // allowRetry: pending 답변이 있으면 다음으로 이동 가능
        if (allowRetry) {
            const pending = pendingAnswersByQuizId.get(currentQuizId)
            // 답변이 있고 비어있지 않으면 OK
            if (pending && pending.length > 0 && pending.some(a => a !== '')) {
                return true
            }
            // 이미 제출된 답변이 있어도 OK
            return resultsByQuizId.has(currentQuizId)
        }

        return resultsByQuizId.has(currentQuizId)
    }, [currentIndex, quizItems.length, resultsByQuizId, currentQuizId, allowRetry, pendingAnswersByQuizId])

    // Check if all questions answered
    const allQuestionsAnswered = useMemo(() => {
        return quizItems.every(item => {
            // 이미 제출된 답변이 있으면 OK
            if (resultsByQuizId.has(item.quizId)) return true
            // allowRetry: pending 답변이 있으면 OK
            if (allowRetry) {
                const pending = pendingAnswersByQuizId.get(item.quizId)
                return pending && pending.length > 0 && pending.some(a => a !== '')
            }
            return false
        })
    }, [quizItems, resultsByQuizId, allowRetry, pendingAnswersByQuizId])

    // Calculate score
    const calculateScore = useMemo(() => {
        const answered = quizItems.filter(item => resultsByQuizId.has(item.quizId))
        const correct = answered.filter(item => resultsByQuizId.get(item.quizId)?.isCorrect)
        return {
            total: quizItems.length,
            answered: answered.length,
            correct: correct.length,
            percentage: answered.length > 0 ? Math.round((correct.length / answered.length) * 100) : 0,
        }
    }, [quizItems, resultsByQuizId])

    // Handle session completion
    const handleCompleteSession = useCallback(async () => {
        // 리뷰 모드에서는 이미 세션이 완료된 상태이므로 API 호출 없이 바로 완료 화면 표시
        if (isReviewMode && completionResponse) {
            setIsCompleted(true)
            return
        }

        setIsCompleting(true)
        try {
            // allowRetry: 마지막 문제의 pending 답변을 먼저 제출
            if (allowRetry) {
                await submitCurrentPendingAnswer()
            }

            const response = await learningSessionApi.completeSession(componentProgressId, {
                componentType: 'quiz',
                forceComplete: false,
                classProductId,
                passScore,
            })
            // skipCompletionScreen이 true면 바로 onComplete 호출 (모듈 마무리 퀴즈 등)
            // allowRetry가 false이고 1회차(첫 시도)에서도 완료 화면 건너뛰고 바로 onComplete 호출
            if (skipCompletionScreen || (!allowRetry && attemptNumber === 1)) {
                onComplete(response)
            } else {
                // allowRetry가 true이거나 2회차 이상에서는 완료 화면 표시
                setCompletionResponse(response)
                setIsCompleted(true)
            }
        } catch (error) {
            console.error('Failed to complete session:', error)
            toast.error(t('learning.quiz.sessionCompleteError'))
        } finally {
            setIsCompleting(false)
        }
    }, [componentProgressId, classProductId, passScore, attemptNumber, onComplete, allowRetry, isReviewMode, completionResponse, submitCurrentPendingAnswer, skipCompletionScreen])

    // Get legacy quiz data for rendering
    const legacyQuiz = currentQuizScript ? getLegacyQuizData(currentQuizScript) : null

    // ========================================================================
    // Render: Loading state
    // ========================================================================
    if (isLoadingQuiz && !currentQuizScript) {
        return (
            <div className="flex flex-col justify-center items-center py-20 bg-white rounded-2xl border border-gray-100 shadow-sm">
                <Loader2 className="w-10 h-10 text-indigo-600 animate-spin" />
                <p className="mt-4 text-gray-600">{t('learning.quiz.loadingQuiz')}</p>
            </div>
        )
    }

    // ========================================================================
    // Render: Completed state
    // ========================================================================
    if (isCompleted) {
        return (
            <div className="overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-sm">
                <div className="p-8 text-center">
                    {/* Trophy Icon */}
                    <div className="flex justify-center items-center mx-auto mb-6 w-24 h-24 bg-indigo-100 rounded-full">
                        <Trophy className="w-12 h-12 text-indigo-600" />
                    </div>

                    {/* Title */}
                    <h2 className="mb-2 text-2xl font-bold text-gray-900">
                        {t('learning.quiz.completedTitle', { quizTitle: getQuizTitle(quizLocation, t) })}
                    </h2>

                    {/* Lesson Title (emphasized) */}
                    <p className="mb-4 text-xl font-semibold text-indigo-600">
                        <ContentMetadataText text={lessonTitle} />
                    </p>

                    {/* Message */}
                    <p className="mb-8 text-gray-600">
                        {t('learning.quiz.completedMessage')}
                    </p>

                    {/* Per-question Results */}
                    <div className="mx-auto mb-8 max-w-md">
                        <h3 className="mb-4 text-sm font-medium text-gray-500">{t('learning.quiz.learningResult')}</h3>
                        <div className="flex flex-wrap gap-2 justify-center">
                            {quizItems.map((quiz, index) => {
                                const result = resultsByQuizId.get(quiz.quizId)
                                const isCorrect = result?.isCorrect ?? false
                                return (
                                    <div
                                        key={quiz.quizId}
                                        className={cn(
                                            "flex justify-center items-center w-10 h-10 text-sm font-medium rounded-lg",
                                            result ? (isCorrect ? "text-emerald-700 bg-emerald-100" : "text-rose-700 bg-rose-100") : "text-gray-400 bg-gray-100"
                                        )}
                                    >
                                        {result ? (
                                            isCorrect ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />
                                        ) : (
                                            index + 1
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-3 justify-center sm:flex-row">
                        {
                            import.meta.env.DEV &&
                            <button
                                onClick={() => {
                                    // 채점결과 확인: 리뷰 모드로 전환
                                    setIsReviewMode(true)
                                    setIsCompleted(false)
                                }}
                                className="flex gap-2 justify-center items-center px-6 py-3 font-semibold text-indigo-600 bg-white rounded-lg border-2 border-indigo-200 transition-all hover:bg-indigo-50 hover:border-indigo-300"
                            >
                                <CheckCircle className="w-5 h-5" />
                                {t('learning.quiz.checkResults')}
                            </button>
                        }
                        <button
                            onClick={() => {
                                if (completionResponse) {
                                    onComplete(completionResponse)
                                } else {
                                    onBack()
                                }
                            }}
                            className="px-8 py-3 font-semibold text-white bg-indigo-600 rounded-lg transition-colors hover:bg-indigo-700"
                        >
                            {t('learning.quiz.goBack')}
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    // ========================================================================
    // Render: No quizzes
    // ========================================================================
    if (quizItems.length === 0) {
        return (
            <div className="overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-sm">
                <div className="flex flex-col justify-center items-center p-8 text-center">
                    <p className="mb-4 text-gray-500">{t('learning.quiz.noQuizzes')}</p>
                    <button
                        onClick={onBack}
                        className="flex gap-2 items-center text-indigo-600 hover:text-indigo-800"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        {t('learning.quiz.goBack')}
                    </button>
                </div>
            </div>
        )
    }

    // ========================================================================
    // Render: Main Quiz UI
    // ========================================================================
    return (
        <div className="overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-sm">
            {/* Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-indigo-600 to-purple-600">
                <div className="flex justify-between items-center">
                    <div>
                        <button
                            onClick={onBack}
                            className="flex gap-2 items-center mb-2 text-sm text-indigo-200 transition-colors hover:text-white"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            {t('learning.quiz.goBack')}
                        </button>
                        <h2 className="text-xl font-bold text-white">{getQuizTitle(quizLocation, t)}</h2>
                        <p className="text-sm text-indigo-200">{getQuizDescription(quizLocation, t)}</p>
                    </div>
                    <div className="text-right">
                        <p className="text-2xl font-bold text-white">
                            {currentIndex + 1} / {quizItems.length}
                        </p>
                        {showImmediateFeedback && (
                            <p className="text-sm text-indigo-200">
                                {t('learning.quiz.correctCount', { correct: calculateScore.correct, total: calculateScore.total })}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Quiz Navigation */}
            <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
                <div className="flex overflow-x-auto gap-2 p-1 custom-scrollbar">
                    {quizItems.map((quiz, index) => {
                        const status = getQuizStatus(index)
                        const quizId = quiz.quizId
                        const isNavigable = canNavigateTo(index)

                        return (
                            <button
                                key={quizId}
                                onClick={() => handleNavClick(index)}
                                disabled={!isNavigable}
                                className={cn(
                                    'flex-shrink-0 w-10 h-10 rounded-lg font-medium text-sm transition-all flex items-center justify-center',
                                    status === 'unsolved' && isNavigable && 'border-2 border-gray-300 bg-white text-gray-600 hover:border-gray-400',
                                    status === 'unsolved' && !isNavigable && 'border-2 border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed opacity-50',
                                    status === 'current' && 'ring-2 ring-indigo-500 ring-offset-2 bg-indigo-600 text-white',
                                    status === 'correct' && 'bg-emerald-500 text-white',
                                    status === 'incorrect' && 'bg-rose-500 text-white',
                                    status === 'submitted' && 'bg-gray-500 text-white'
                                )}
                            >
                                {status === 'correct' && <CheckCircle className="w-5 h-5" />}
                                {status === 'incorrect' && <XCircle className="w-5 h-5" />}
                                {status === 'submitted' && <CheckCircle className="w-5 h-5" />}
                                {(status === 'unsolved' || status === 'current') && (index + 1)}
                            </button>
                        )
                    })}
                </div>
            </div>

            {/* Quiz Content */}
            <div className="p-6">
                {isLoadingQuiz ? (
                    <div className="flex flex-col justify-center items-center py-12">
                        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                        <p className="mt-3 text-gray-500">{t('learning.quiz.loadingQuestion')}</p>
                    </div>
                ) : legacyQuiz ? (
                    <div className="mx-auto max-w-3xl">
                        <QuizRenderer
                            key={`${currentQuizId}-${isReviewMode ? 'review' : (allowRetry ? 'retry' : (currentResult ? 'solved' : 'unsolved'))}`}
                            quizType={legacyQuiz.type}
                            quizId={currentQuizId}
                            quiz={legacyQuiz}
                            onSolve={handleSolve}
                            solveResult={getSolveResultForDisplay(currentQuizId)}
                            showReallyTrue={false}
                            useKeypad={false}
                            initialAnswers={currentSubmittedAnswers}
                            hideSubmitButton={allowRetry}
                            onAnswerChange={handleAnswerChange}
                            disableSound={isReviewMode || answeredQuizIdsSet.has(currentQuizId) || soundPlayedQuizIds.has(currentQuizId)}
                        />

                        {isSolving && (
                            <div className="flex gap-2 justify-center items-center mt-4 text-gray-500">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span className="text-sm">{t('learning.quiz.grading')}</span>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="flex flex-col justify-center items-center py-12">
                        <p className="text-gray-500">{t('learning.quiz.cannotLoadData')}</p>
                        <button
                            onClick={() => loadQuizScript(currentQuizId)}
                            className="flex gap-2 items-center mt-4 text-indigo-600 hover:text-indigo-800"
                        >
                            <RefreshCw className="w-4 h-4" />
                            {t('learning.quiz.retry')}
                        </button>
                    </div>
                )}
            </div>

            {/* Footer Navigation */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <div className="flex justify-between items-center mx-auto max-w-3xl">
                    <button
                        onClick={handlePrevious}
                        disabled={currentIndex === 0}
                        className={cn(
                            'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors',
                            currentIndex === 0
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-gray-700 hover:bg-gray-100'
                        )}
                    >
                        <ChevronLeft className="w-5 h-5" />
                        {t('learning.quiz.previous')}
                    </button>

                    <span className="text-sm text-gray-500">
                        {currentIndex + 1} / {quizItems.length}
                    </span>

                    {currentIndex < quizItems.length - 1 ? (
                        <button
                            onClick={handleNext}
                            disabled={!canGoNext}
                            className={cn(
                                'flex gap-2 items-center px-4 py-2 font-medium rounded-lg transition-colors',
                                !canGoNext
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-indigo-600 hover:bg-indigo-50'
                            )}
                        >
                            {t('learning.quiz.next')}
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    ) : (
                        <button
                            onClick={handleCompleteSession}
                            disabled={!allQuestionsAnswered || isCompleting}
                            className={cn(
                                'flex gap-2 items-center px-6 py-2 font-medium rounded-lg transition-colors',
                                !allQuestionsAnswered || isCompleting
                                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                            )}
                        >
                            {isCompleting ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    {t('learning.quiz.completing')}
                                </>
                            ) : (
                                <>
                                    {t('learning.quiz.complete')}
                                    <CheckCircle className="w-5 h-5" />
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}

export default QuizStage
